/**
 * TransactionDetail Page
 * 
 * Displays detailed information for a single transaction.
 * Desktop: Clean sectioned layout with unified appearance
 * Mobile: Card-based layout for better scrolling
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { logger } from '@/app/utils/logger';
import { TransactionTypeBadge, SourceSystemBadge } from '@/components/common/Badge';
import { TransactionContext } from '@/components/transactions/TransactionContext';
import { TransactionResponse } from '@/app/types/transactions';
import { transactionService, auditService } from '@/app/services';

import './TransactionDetail.scss';

export const TransactionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [transaction, setTransaction] = useState<TransactionResponse | null>(null);
  const [relatedAdjustments, setRelatedAdjustments] = useState<TransactionResponse[]>([]);
  const [userBalance, setUserBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const detail = await transactionService.getTransactionById(id);
        if (!detail) {
          setError('Transaction not found');
          return;
        }
        
        // Single API call returns everything we need
        setTransaction(detail.transaction);
        setRelatedAdjustments(detail.related_adjustments);
        setUserBalance(detail.user_balance);
        
        // Log record view for audit
        auditService.logRecordView('transaction', id, {
          page_url: `/transactions/${id}`,
        }).catch((err) => logger.warn('Failed to log record view', { error: err }));
      } catch (err) {
        setError('Failed to load transaction');
        logger.error('Failed to load transaction', { error: err, transactionId: id });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
      timeZoneName: 'short',
    });
  };

  const formatPoints = (value: number) => {
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toLocaleString()} pts`;
  };

  const truncateUuid = (uuid: string) => {
    return `${uuid.slice(0, 8)}...${uuid.slice(-4)}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  if (loading) {
    return (
      <div className="transaction-detail-page">
        <div className="loading-state">
          <div className="spinner" />
          <span>Loading transaction...</span>
        </div>
      </div>
    );
  }

  if (error || !transaction) {
    return (
      <div className="transaction-detail-page">
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <span className="error-message">{error || 'Transaction not found'}</span>
          <button onClick={() => navigate('/transactions')} className="btn-back">
            Back to Transactions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="transaction-detail-page">
      {/* Back Navigation */}
      <div className="back-nav">
        <button onClick={() => navigate('/transactions')} className="btn-back-link">
          ← Back to Transactions
        </button>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="header-left">
          <div className="header-badges">
            <TransactionTypeBadge type={transaction.transaction_type} />
            <SourceSystemBadge source={transaction.source_system} />
          </div>
        </div>
        <div className="header-meta">
          <span className="header-date">{formatDate(transaction.created_at)}</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="detail-container">
        {/* Transaction Info Section */}
        <section className="detail-section">
          <h2 className="section-title">Transaction Information</h2>
          <div className="info-grid">
            <div className="info-row">
              <span className="info-label">Transaction ID</span>
              <span className="info-value mono">
                {transaction.transaction_id}
                <button className="copy-btn" onClick={() => copyToClipboard(transaction.transaction_id)}>📋</button>
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">Type</span>
              <span className="info-value"><TransactionTypeBadge type={transaction.transaction_type} /></span>
            </div>
            <div className="info-row">
              <span className="info-label">Point Value</span>
              <span className={`info-value points ${transaction.point_value >= 0 ? 'positive' : 'negative'}`}>
                {formatPoints(transaction.point_value)}
              </span>
            </div>
            {transaction.dollar_value && (
              <div className="info-row">
                <span className="info-label">Dollar Value</span>
                <span className="info-value">
                  {transaction.dollar_value 
                    ? `$${typeof transaction.dollar_value === 'string' 
                      ? parseFloat(transaction.dollar_value).toFixed(2) 
                      : transaction.dollar_value.toFixed(2)}`
                    : '—'}
                </span>
              </div>
            )}
            <div className="info-row">
              <span className="info-label">Source System</span>
              <span className="info-value"><SourceSystemBadge source={transaction.source_system} /></span>
            </div>
            {transaction.transaction_title && (
              <div className="info-row">
                <span className="info-label">Title</span>
                <span className="info-value">{transaction.transaction_title}</span>
              </div>
            )}
            {transaction.transaction_subtitle && (
              <div className="info-row">
                <span className="info-label">Subtitle</span>
                <span className="info-value">{transaction.transaction_subtitle}</span>
              </div>
            )}
          </div>
        </section>

        {/* User Section */}
        <section className="detail-section">
          <h2 className="section-title">User Information</h2>
          <div className="info-grid two-col">
            <div className="info-group">
              <div className="info-row">
                <span className="info-label">User ID</span>
                <span className="info-value">
                  <Link to={`/users/${transaction.community_ref_user_id}`} className="entity-link">
                    {transaction.community_ref_user_id} →
                  </Link>
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Participant ID</span>
                <span className="info-value">{transaction.eligible_participant_id?.toString() || 'Not verified'}</span>
              </div>
            </div>
            <div className="info-group">
              <div className="info-row">
                <span className="info-label">Partner Product ID</span>
                <span className="info-value">{transaction.partner_product_id?.toString() || '—'}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Current Balance</span>
                <span className="info-value balance">{userBalance.toLocaleString()} pts</span>
              </div>
            </div>
          </div>
        </section>

        {/* Reward Section (for REDEEM transactions) */}
        {transaction.transaction_type === 'redeem' && transaction.reward_id && (
          <section className="detail-section">
            <h2 className="section-title">Reward Details</h2>
            <div className="info-grid">
              <div className="info-row">
                <span className="info-label">Reward</span>
                <span className="info-value">
                  <Link to={`/rewards/${transaction.reward_id}`} className="entity-link">
                    View Reward →
                  </Link>
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Reward ID</span>
                <span className="info-value mono small">
                  {transaction.reward_id}
                  <button className="copy-btn" onClick={() => copyToClipboard(transaction.reward_id!)}>📋</button>
                </span>
              </div>
            </div>
          </section>
        )}

        {/* Timestamps Section */}
        <section className="detail-section">
          <h2 className="section-title">Timeline</h2>
          <div className="timeline-grid">
            <div className="timeline-item">
              <span className="timeline-label">Transaction Date</span>
              <span className="timeline-value">{formatDate(transaction.transaction_datetime_utc)}</span>
            </div>
            <div className="timeline-item muted">
              <span className="timeline-label">Created</span>
              <span className="timeline-value">{formatDate(transaction.created_at)}</span>
            </div>
          </div>
        </section>

        {/* Context Section */}
        <section className="detail-section">
          <h2 className="section-title">Transaction Context</h2>
          <TransactionContext 
            context={transaction.context_json} 
            transactionId={transaction.transaction_id}
          />
        </section>

        {/* Related Adjustments Section */}
        {relatedAdjustments.length > 0 && (
          <section className="detail-section">
            <h2 className="section-title">Related Adjustments</h2>
            <p className="section-subtitle">{relatedAdjustments.length} adjustment(s) reference this transaction</p>
            <div className="related-list">
              {relatedAdjustments.map((adj) => (
                <Link 
                  key={adj.transaction_id} 
                  to={`/transactions/${adj.transaction_id}`}
                  className="related-item"
                >
                  <div className="related-item-main">
                    <span className="related-id">{truncateUuid(adj.transaction_id)}</span>
                    <TransactionTypeBadge type={adj.transaction_type} />
                  </div>
                  <div className="related-item-meta">
                    <span className={`related-points ${adj.point_value >= 0 ? 'positive' : 'negative'}`}>
                      {formatPoints(adj.point_value)}
                    </span>
                    <span className="related-date">
                      {new Date(adj.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default TransactionDetail;
